import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Trash2,
  Upload
} from "lucide-react";

interface FormData {
  name: string;
  categories: string[];
  description: string;
  websiteUrl: string;
  suburbs: string[];
  imageUrl: string;
  isFeatured: boolean;
  isVerified: boolean;
}

interface AdminEditListingPreviewProps {
  formData: FormData;
  imagePreview: string | null;
  onImageChange: (file: File) => void;
}

const AdminEditListingPreview = ({ 
  formData, 
  imagePreview, 
  onImageChange
}: AdminEditListingPreviewProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please select an image file (JPEG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "The image must be less than 5MB",
        variant: "destructive",
      });
      return;
    }
    
    onImageChange(file);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-8">
      <div className="h-48 overflow-hidden bg-gray-100 relative">
        {(imagePreview || formData.imageUrl) ? (
          <img
            src={imagePreview || formData.imageUrl}
            alt={formData.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No image available
          </div>
        )}
        <input 
          type="file" 
          ref={fileInputRef}
          onChange={handleImageChange}
          className="hidden"
          accept="image/*"
        />
        <Button 
          variant="secondary" 
          size="sm" 
          className="absolute bottom-2 right-2 bg-white/80 hover:bg-white"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1" />
          Change Image
        </Button>
      </div>
      
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-2">{formData.name}</h3>
        
        <div className="flex flex-wrap gap-1 mb-2">
          {formData.categories.map((cat, index) => (
            <Badge key={index} variant="outline">
              {cat}
            </Badge>
          ))}
        </div>
        
        <p className="text-sm text-gray-600 mb-4 line-clamp-3">
          {formData.description}
        </p>
        
        {formData.websiteUrl && (
          <div className="mb-3 flex items-center text-sm text-whatsapp">
            <span className="font-medium">Website: </span>
            <a 
              href={formData.websiteUrl.startsWith('http') ? formData.websiteUrl : `https://${formData.websiteUrl}`} 
              target="_blank" 
              rel="noreferrer"
              className="ml-1 hover:underline truncate"
            >
              {formData.websiteUrl}
            </a>
          </div>
        )}
        
        <div className="flex flex-wrap gap-1 mb-2">
          {formData.suburbs.map((suburb, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {suburb}
            </Badge>
          ))}
        </div>
        
        <div className="flex flex-col gap-2 mt-4 pt-4 border-t">
          <div className="flex items-center">
            <span className="font-medium mr-2">Featured:</span>
            {formData.isFeatured ? (
              <Badge className="border border-whatsapp/20 bg-whatsapp-light text-whatsapp">
                Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-600">
                No
              </Badge>
            )}
          </div>
          
          <div className="flex items-center">
            <span className="font-medium mr-2">Verified:</span>
            {formData.isVerified ? (
              <Badge className="bg-green-100 text-green-800 border border-green-200">
                Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-600">
                No
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditListingPreview;
